import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { LangConstant, SystemConstant } from "@/const";
import { LodashUtils } from "@/utils";
import { ResponseRequestType, ResponseUserType } from "@/types/service";
import { RequestMode } from "@/types/common";
import { SelectedRequests, SelectedUsers } from "./components";
import AppDialog, { AppDialogProps } from "../AppDialog";
import { useExportRequest } from "./hooks";
import LoadingEncryptDialog from "../LoadingEncryptDialog";

export type ExportDialogProps = AppDialogProps & {
  mode: RequestMode;
  requests: ResponseRequestType[];
  onStart: () => void;
  onSuccess: (file: ArrayBuffer) => void;
  onFailure: () => void;
};

const STEPS = {
  showRequest: 1,
  selectUser: 2,
};

const ExportDialog: FC<ExportDialogProps> = props => {
  const { open, mode, requests } = props;
  const { onClose, onStart, onSuccess, onFailure } = props;

  const { t: getLabel } = useTranslation();
  const { exportRequest, updateRequestStatus } = useExportRequest();

  const [step, setStep] = useState<number>(STEPS.showRequest);
  const [users, setUsers] = useState<ResponseUserType[]>([]);
  const [error, setError] = useState<string>("");
  const [isOpenEncryptDialog, setIsOpenEncryptDialog] = useState<boolean>(false);

  const handleClose = () => {
    setUsers([]);
    setStep(STEPS.showRequest);
    setError("");
    onClose();
  };

  const handleExportRequests = async () => {
    setIsOpenEncryptDialog(false);
    onStart();
    const status =
      mode === "internal" ? SystemConstant.STATUS_REQUEST.pendingSend : SystemConstant.STATUS_REQUEST.success;
    const requestUidList = requests.map(req => req.transferRequestUid);
    const file = await exportRequest({ mode, users, requestUidList });
    if (file) {
      const isUpdateStatusSuccess = await updateRequestStatus({ requestUidList, status });
      if (isUpdateStatusSuccess) onSuccess(file);
      else onFailure();
    } else {
      onFailure();
    }
    setUsers([]);
    setStep(STEPS.showRequest);
    setError("");
  };

  const handleOpenEncrypt = () => {
    setIsOpenEncryptDialog(true);
    onClose();
  };

  const handleClickContinue = () => {
    if (step === STEPS.showRequest) {
      setStep(STEPS.selectUser);
    } else if (step === STEPS.selectUser) {
      if (users.length > 0) {
        handleOpenEncrypt();
      } else {
        setError(getLabel("MSG_NO_USER_SELECTED", { ns: LangConstant.NS_REQUEST }));
      }
    }
  };

  const handleRemoveUser = (user: ResponseUserType) => {
    setUsers(state => {
      return state.filter(currentUser => !LodashUtils.isEqual(currentUser, user));
    });
  };

  const handleAddUser = (user: ResponseUserType) => {
    if (LodashUtils.includes(users, user)) {
      handleRemoveUser(user);
    } else {
      setUsers(state => [...state, user]);
    }
  };

  return (
    <>
      <AppDialog
        fullWidth
        open={open}
        onClose={handleClose}
        CancelButtonProps={{
          onClick: handleClose,
        }}
        ConfirmButtonProps={{
          label: getLabel("L_CONTINUE"),
          disabled: requests.length === 0,
          onClick: handleClickContinue,
        }}
        title={getLabel("L_EXPORT_REQUEST_TITLE", {
          ns: LangConstant.NS_REQUEST,
        })}
      >
        {step === STEPS.showRequest && <SelectedRequests requests={requests} />}
        {step === STEPS.selectUser && (
          <>
            <SelectedUsers users={users} addUser={handleAddUser} removeUser={handleRemoveUser} />
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </>
        )}
      </AppDialog>
      <LoadingEncryptDialog open={isOpenEncryptDialog} onClose={handleExportRequests} />
    </>
  );
};

export default ExportDialog;
